import React, { useEffect, useState } from 'react';
import { Award, Sparkles, Trophy } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { AaiHome } from '../../types/aai';
import { Loading, ErrorNote } from './components';
import { PageHeader } from './dashboards/ui';

// Champion · Spotlights (design §7.4) — recognition, never ranking. The feed is
// surfaced by the champion home lens (people showing momentum), so there's no
// manual "add" here: it's positive framing derived from progress, not a
// leaderboard you curate.
const SpotlightsPage: React.FC = () => {
  const [spotlights, setSpotlights] = useState<{ who: string; what: string }[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await aaiApi.getHome() as { data: AaiHome };
        if (data.role === 'champion') setSpotlights(data.spotlights || []);
        else setUnavailable(true);
      } catch {
        setError('Could not load spotlights.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader
        kicker="AI Adoption"
        title="Spotlights"
        subtitle="Recognition, never ranking. Celebrate wins and momentum — there's no leaderboard, no bottom of a list."
      />

      <div className="flex items-center gap-3.5 bg-black text-white rounded-aa px-5 py-4 mb-4">
        <span className="w-[38px] h-[38px] rounded-[10px] bg-accent-500/20 text-accent-500 grid place-items-center shrink-0"><Trophy size={18} /></span>
        <div className="flex-1">
          <div className="font-bold text-[13.5px]">Recognition, never ranking.</div>
          <div className="text-[12.5px] text-white/65 leading-snug">Surface high performers and clever hacks for leadership to celebrate — peer recognition turns adoption into a grassroots movement.</div>
        </div>
      </div>

      {loading ? <Loading label="Loading spotlights…" /> : error ? <ErrorNote message={error} /> : unavailable ? (
        <p className="text-sm text-gray-500">Spotlights are available to the AI champion.</p>
      ) : spotlights.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-xl p-8 text-center">
          <Award size={28} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No spotlights yet — they appear as people build momentum and ship skills.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {spotlights.map((sp, i) => (
            <div key={i} className="aa-lift bg-card rounded-aa p-5">
              <span className="inline-block bg-accent-500 text-black font-sans text-[10px] font-bold uppercase tracking-[0.05em] rounded-full px-2.5 py-0.5 mb-3">Spotlight</span>
              <div className="font-bold text-[14.5px] mb-1.5">{sp.who}</div>
              <p className="text-[13px] text-ink-muted leading-[1.55] m-0">{sp.what}</p>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && !unavailable && (
        <div className="mt-4 flex items-start gap-2.5 bg-brand-50 rounded-xl p-4">
          <Sparkles size={16} className="text-brand-500 mt-0.5 shrink-0" />
          <p className="text-[12.5px] text-gray-600 m-0 leading-relaxed">
            Spotlights are surfaced automatically from momentum and shipped skills — positive framing only, no ranking.
            They also appear on your company dashboard and in the board readout.
          </p>
        </div>
      )}
    </div>
  );
};

export default SpotlightsPage;
